import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  BedDouble,
  BriefcaseBusiness,
  Boxes,
  Building2,
  ConciergeBell,
  LoaderCircle,
  Search,
  Sparkles,
  SquareStack,
  Tag,
  Ticket,
  User,
  UserRoundCog,
  Users,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useHotel } from "@/hooks/useHotel";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { LocalStorageKeys } from "@/components/pages/login/hooks/useLocalStorage";
import {
  filterGlobalSearchNavigationItems,
  getGlobalSearchNavigationItems,
  resolveGlobalSearchModuleHref,
} from "@/components/layout/components/global-search/navigationSearch";
import type { UserProfile } from "@/services/authService";
import type {
  GlobalSearchEntityType,
  GlobalSearchItem,
  GlobalSearchResponse,
} from "@/services/global-search-service";
import {
  hasCorePlatformAccess,
  isPlatformAdminRole,
} from "@/utils/accessControl";

interface GlobalSearchSectionConfig {
  key: GlobalSearchSectionKey;
  labelKey: string;
}

interface ResolvedSearchItem extends GlobalSearchItem {
  href: string;
}

interface SearchSection {
  key: GlobalSearchSectionKey;
  label: string;
  items: ResolvedSearchItem[];
}

type GlobalSearchSectionKey = Exclude<keyof GlobalSearchResponse, "query">;
type GlobalSearchCapabilities = ReturnType<
  typeof useAccessCapabilities
>["capabilities"];

interface SearchResultsListProps {
  activeIndex: number;
  flatItems: ResolvedSearchItem[];
  listboxId: string;
  onClose: () => void;
  onHoverItem: (index: number) => void;
  sections: SearchSection[];
  t: TFunction;
}

interface SearchResultItemProps {
  isActive: boolean;
  item: ResolvedSearchItem;
  itemIndex: number;
  onClose: () => void;
  onHover: (index: number) => void;
}

const SECTION_ORDER: GlobalSearchSectionConfig[] = [
  { key: "tenants", labelKey: "layout.globalSearch.sections.tenants" },
  { key: "hotels", labelKey: "layout.globalSearch.sections.hotels" },
  {
    key: "reservations",
    labelKey: "layout.globalSearch.sections.reservations",
  },
  { key: "guests", labelKey: "layout.globalSearch.sections.guests" },
  { key: "rooms", labelKey: "layout.globalSearch.sections.rooms" },
  {
    key: "roomCategories",
    labelKey: "layout.globalSearch.sections.roomCategories",
  },
  { key: "services", labelKey: "layout.globalSearch.sections.services" },
  {
    key: "serviceCategories",
    labelKey: "layout.globalSearch.sections.serviceCategories",
  },
  { key: "amenities", labelKey: "layout.globalSearch.sections.amenities" },
  { key: "requests", labelKey: "layout.globalSearch.sections.requests" },
  { key: "staff", labelKey: "layout.globalSearch.sections.staff" },
  { key: "teams", labelKey: "layout.globalSearch.sections.teams" },
  { key: "categories", labelKey: "layout.globalSearch.sections.categories" },
  { key: "modules", labelKey: "layout.globalSearch.sections.modules" },
];

const getSearchItemIcon = (type: GlobalSearchEntityType) => {
  switch (type) {
    case "tenant":
    case "hotel":
      return Building2;
    case "reservation":
      return BriefcaseBusiness;
    case "guest":
      return User;
    case "room":
      return BedDouble;
    case "roomCategory":
      return Building2;
    case "service":
      return ConciergeBell;
    case "serviceCategory":
      return SquareStack;
    case "amenity":
      return Sparkles;
    case "request":
      return Ticket;
    case "staff":
      return UserRoundCog;
    case "team":
      return Users;
    case "category":
      return Tag;
    case "module":
      return Boxes;
  }
};

const EXCLUDED_MODULE_KEYS = new Set(["hotels_limit", "rooms_limit"]);

const CORE_SEARCH_ITEM_HREFS: Partial<
  Record<GlobalSearchEntityType, (id: string) => string>
> = {
  reservation: (id) => `/reservations/${id}`,
  guest: (id) => `/reservations/guests/${id}`,
  room: (id) => `/reservations/rooms/${id}`,
  roomCategory: (id) => `/reservations/rooms/categories/${id}`,
  amenity: (id) => `/amenities/${id}`,
};

const CAPABILITY_SEARCH_ITEM_HREFS: Partial<
  Record<GlobalSearchEntityType, (id: string) => string>
> = {
  service: (id) => `/services/search/${id}`,
  serviceCategory: () => "/services/categories",
  request: (id) => `/requests/${id}`,
  staff: (id) => `/staff/${id}`,
  team: (id) => `/staff/teams/${id}`,
};

const PLATFORM_SEARCH_ITEM_HREFS: Partial<
  Record<GlobalSearchEntityType, (item: GlobalSearchItem) => string>
> = {
  tenant: (item) => `/tenants/${item.id}/edit`,
  hotel: () => "/hotels",
};

const isCoreSearchItemAccessible = (
  item: GlobalSearchItem,
  userProfile: UserProfile | null,
): boolean => {
  switch (item.type) {
    case "reservation":
      return hasCorePlatformAccess(userProfile, "reservations");
    case "guest":
      return hasCorePlatformAccess(userProfile, "guests");
    case "room":
    case "roomCategory":
      return hasCorePlatformAccess(userProfile, "rooms");
    case "amenity":
      return hasCorePlatformAccess(userProfile, "amenities");
    default:
      return false;
  }
};

const isCapabilitySearchItemAccessible = (
  item: GlobalSearchItem,
  capabilities: GlobalSearchCapabilities,
): boolean => {
  switch (item.type) {
    case "service":
    case "serviceCategory":
      return capabilities.canManageServices;
    case "request":
      return capabilities.canUseRequestsSystem;
    case "staff":
    case "team":
      return capabilities.canManageStaffOperations;
    default:
      return false;
  }
};

const isPlatformSearchItemAccessible = (
  item: GlobalSearchItem,
  userProfile: UserProfile | null,
): boolean => {
  switch (item.type) {
    case "tenant":
      return isPlatformAdminRole(userProfile);
    case "hotel":
      return (
        isPlatformAdminRole(userProfile) || userProfile?.role === "TENANT_ADMIN"
      );
    default:
      return false;
  }
};

const isVisibleModuleSearchItem = (item: GlobalSearchItem): boolean => {
  const moduleKey = item.subtitle?.trim().toLowerCase();

  return item.type !== "module" || !EXCLUDED_MODULE_KEYS.has(moduleKey ?? "");
};

const resolveEntitySearchItemHref = (
  item: GlobalSearchItem,
  userProfile: UserProfile | null,
  capabilities: GlobalSearchCapabilities,
): string | null => {
  const platformHrefBuilder = PLATFORM_SEARCH_ITEM_HREFS[item.type];
  if (platformHrefBuilder) {
    return isPlatformSearchItemAccessible(item, userProfile)
      ? platformHrefBuilder(item)
      : null;
  }

  const coreHrefBuilder = CORE_SEARCH_ITEM_HREFS[item.type];
  if (coreHrefBuilder) {
    return isCoreSearchItemAccessible(item, userProfile)
      ? coreHrefBuilder(item.id)
      : null;
  }

  const capabilityHrefBuilder = CAPABILITY_SEARCH_ITEM_HREFS[item.type];
  if (capabilityHrefBuilder) {
    return isCapabilitySearchItemAccessible(item, capabilities)
      ? capabilityHrefBuilder(item.id)
      : null;
  }

  return null;
};

const toResolvedSearchItem = (
  item: GlobalSearchItem,
  href: string | null,
): ResolvedSearchItem | null => {
  if (!href) {
    return null;
  }

  return {
    ...item,
    href,
  };
};

const mergeResolvedItems = (
  items: ResolvedSearchItem[],
): ResolvedSearchItem[] =>
  Array.from(new Map(items.map((item) => [item.href, item])).values());

const getSearchSections = (
  response: GlobalSearchResponse | undefined,
  navigationItems: ReturnType<typeof getGlobalSearchNavigationItems>,
  query: string,
  userProfile: UserProfile | null,
  capabilities: GlobalSearchCapabilities,
  t: TFunction,
): SearchSection[] => {
  const navigationModuleItems = filterGlobalSearchNavigationItems(
    navigationItems,
    query,
  ).map(
    (item): ResolvedSearchItem => ({
      href: item.href,
      id: item.id,
      label: item.label,
      subtitle: item.subtitle,
      type: item.type,
    }),
  );

  return SECTION_ORDER.map((sectionConfig) => {
    const responseItems = (response?.[sectionConfig.key] ?? []).filter(
      isVisibleModuleSearchItem,
    );
    const resolvedResponseItems = responseItems
      .map((item) => {
        const href =
          item.type === "module"
            ? resolveGlobalSearchModuleHref(item, navigationItems)
            : resolveEntitySearchItemHref(item, userProfile, capabilities);

        return toResolvedSearchItem(item, href);
      })
      .filter((item): item is ResolvedSearchItem => item !== null);

    const items =
      sectionConfig.key === "modules"
        ? mergeResolvedItems([
            ...resolvedResponseItems,
            ...navigationModuleItems,
          ])
        : resolvedResponseItems;

    return {
      key: sectionConfig.key,
      label: t(sectionConfig.labelKey),
      items,
    };
  }).filter((section) => section.items.length > 0);
};

const flattenSearchItems = (
  sections: SearchSection[],
): ResolvedSearchItem[] => {
  return sections.flatMap((section) => section.items);
};

const SearchResultItem: React.FC<SearchResultItemProps> = ({
  isActive,
  item,
  itemIndex,
  onClose,
  onHover,
}) => {
  const Icon = getSearchItemIcon(item.type);
  const itemContent = (
    <div
      id={`global-search-item-${item.type}-${item.id}`}
      role="option"
      aria-selected={isActive}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors",
        isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/70",
      )}
      onMouseEnter={() => onHover(itemIndex)}
    >
      <div className="mt-0.5 rounded-md bg-accent/80 p-2 text-muted-foreground">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {item.label}
        </p>
        {item.subtitle ? (
          <p className="truncate text-sm text-muted-foreground">
            {item.subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <Link to={item.href} onClick={onClose} className="block">
      {itemContent}
    </Link>
  );
};

const SearchResultsList: React.FC<SearchResultsListProps> = ({
  activeIndex,
  flatItems,
  listboxId,
  onClose,
  onHoverItem,
  sections,
  t,
}) => {
  return (
    <div
      id={listboxId}
      role="listbox"
      aria-label={t("layout.globalSearch.resultsAriaLabel")}
      className="flex flex-col gap-1"
    >
      {sections.map((section) => (
        <div key={section.key} className="rounded-lg">
          <div className="px-3 pt-3 pb-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                {section.label}
              </p>
              <span className="text-xs text-muted-foreground">
                {t("layout.globalSearch.sectionCount", {
                  count: section.items.length,
                })}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-1 px-1 pb-2">
            {section.items.map((item) => {
              const itemIndex = flatItems.findIndex(
                (flatItem) =>
                  flatItem.type === item.type && flatItem.id === item.id,
              );

              return (
                <SearchResultItem
                  key={`${item.type}-${item.id}`}
                  item={item}
                  itemIndex={itemIndex}
                  isActive={itemIndex === activeIndex}
                  onClose={onClose}
                  onHover={onHoverItem}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export const GlobalSearch: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const listboxId = useId();
  const { userProfile } = useAuthenticatedUser();
  const { hotelId } = useHotel();
  const { capabilities } = useAccessCapabilities();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 300);
  const normalizedQuery = query.trim();
  const hasEnoughCharacters = normalizedQuery.length >= 2;
  const tenantId =
    userProfile?.tenantId ??
    localStorage.getItem(LocalStorageKeys.TENANT_ID) ??
    undefined;

  const searchRef = useClickOutside<HTMLDivElement>(() => {
    setIsOpen(false);
    setActiveIndex(0);
  }, isOpen);

  const { data, isFetching, isError } = useGlobalSearch({
    hotelId,
    tenantId,
    query: debouncedQuery,
  });
  const navigationItems = useMemo(
    () =>
      getGlobalSearchNavigationItems({
        capabilities,
        t,
        userProfile,
      }),
    [capabilities, t, userProfile],
  );

  const sections = useMemo(
    () =>
      getSearchSections(
        data,
        navigationItems,
        normalizedQuery,
        userProfile,
        capabilities,
        t,
      ),
    [capabilities, data, navigationItems, normalizedQuery, t, userProfile],
  );
  const flatItems = flattenSearchItems(sections);
  const hasResults = flatItems.length > 0;
  const shouldShowPanel = isOpen && normalizedQuery.length > 0;
  const activeItem = flatItems[activeIndex];
  const inputActiveDescendant =
    shouldShowPanel && activeItem
      ? `global-search-item-${activeItem.type}-${activeItem.id}`
      : undefined;
  const inputIcon = isFetching ? (
    <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
  ) : (
    <Search className="size-4 text-muted-foreground" />
  );

  useEffect(() => {
    setIsOpen(false);
    setActiveIndex(0);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    if (!flatItems.length) {
      setActiveIndex(0);
      return;
    }

    if (activeIndex > flatItems.length - 1) {
      setActiveIndex(flatItems.length - 1);
    }
  }, [activeIndex, flatItems]);

  const closePanel = () => {
    setIsOpen(false);
    setActiveIndex(0);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    setQuery("");
    setIsOpen(false);
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!shouldShowPanel) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closePanel();
      return;
    }

    if (!flatItems.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((currentIndex) => (currentIndex + 1) % flatItems.length);
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((currentIndex) =>
        currentIndex === 0 ? flatItems.length - 1 : currentIndex - 1,
      );
    }

    if (event.key === "Enter" && activeItem) {
      event.preventDefault();
      closePanel();
      navigate(activeItem.href);
    }
  };

  let panelContent: React.ReactNode = null;

  if (!hasEnoughCharacters) {
    panelContent = (
      <div className="flex items-center justify-center rounded-lg px-4 py-10 text-sm text-muted-foreground">
        {t("layout.globalSearch.minCharactersHint")}
      </div>
    );
  } else if (hasResults) {
    panelContent = (
      <SearchResultsList
        activeIndex={activeIndex}
        flatItems={flatItems}
        listboxId={listboxId}
        onClose={closePanel}
        onHoverItem={setActiveIndex}
        sections={sections}
        t={t}
      />
    );
  } else if (isFetching) {
    panelContent = (
      <div className="flex flex-col gap-2 p-2">
        {Array.from({ length: 6 }, (_, index) => (
          <div
            key={index}
            className="h-14 animate-pulse rounded-lg bg-accent/60"
          />
        ))}
      </div>
    );
  } else if (isError) {
    panelContent = (
      <div className="flex items-center justify-center rounded-lg px-4 py-10 text-sm text-muted-foreground">
        {t("layout.globalSearch.error")}
      </div>
    );
  } else if (!hasResults) {
    panelContent = (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg px-4 py-10 text-center">
        <Search className="size-5 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          {t("layout.globalSearch.emptyTitle")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("layout.globalSearch.emptyDescription", {
            query: normalizedQuery,
          })}
        </p>
      </div>
    );
  }

  return (
    <div ref={searchRef} className="relative w-full max-w-3xl">
      <Input
        ref={inputRef}
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={t("layout.searchPlaceholder")}
        aria-label={t("layout.globalSearch.inputAriaLabel")}
        aria-expanded={shouldShowPanel}
        aria-controls={shouldShowPanel ? listboxId : undefined}
        aria-activedescendant={inputActiveDescendant}
        icon={inputIcon}
        className="min-w-[20rem] pr-10"
      />
      {query.length > 0 ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
          aria-label={t("layout.globalSearch.clear")}
        >
          <span className="text-sm">{t("layout.globalSearch.clearShort")}</span>
        </button>
      ) : null}

      {shouldShowPanel ? (
        <div className="absolute top-[calc(100%+0.5rem)] left-0 z-50 w-full overflow-hidden rounded-xl border border-border bg-background shadow-lg">
          <div className="max-h-[28rem] overflow-y-auto p-2">
            {panelContent}
          </div>
        </div>
      ) : null}
    </div>
  );
};
